import { createAction, createAsyncThunk, createSlice, isFulfilled, isPending, isRejected,
    PayloadAction } from '@reduxjs/toolkit';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ContainerType } from 'src/components/common/container-details/ContainerDetailsModel';
import { buildGetContainerDetailsInput, GetContainerDetailsInput, GetContainerDetailsOutput, GetContainerMode }
    from 'src/models/GetContainerDetails';
import { CreateContainerInput } from 'src/models/createContainer';
import { CloseResult, DoorScanResult, FailureResultType, LabelScanResult, ResolvedSideline, ResolveResult, ScanResult,
    ScanResultType, SidelineCheckResult, Trailer } from 'src/models/unload';
import { getBoundContainerId, getBoundContainerName, getChildContainerCount, getDoorScanResult, getFailureResult,
    getLabelScanResult, getPalletScanResult, getSidelineCheckResult } from 'src/utils/unload-scan-results';
import { resetIsLoading, setIsLoading } from 'src/store/common-slices/uiSlice';
import RIVBFFService from 'src/services/riv-bff-service';
import { RIVApiMethods } from 'src/constants/api-method-constants';
import { UNLOAD_TIMED_OUT } from 'src/localization/string-ids/app-strings';
import { UNKNOWN_ERROR } from 'src/localization/string-ids/error-constants';
import { playScanTone, ScanTone } from 'src/utils/scan-audio';
import { recordFatalEvents } from 'src/utils/fatal-metrics';
import type { AppDispatch, RootState } from 'src/store/store';

export enum UnloadPages {
    SCAN_DOOR_PAGE = 'SCAN_DOOR_PAGE',
    SCAN_CONTAINER_PAGE = 'SCAN_CONTAINER_PAGE',
    SCAN_ORPHAN_PAGE = 'SCAN_ORPHAN_PAGE',
    SCAN_SIDELINE_PAGE = 'SCAN_SIDELINE_PAGE',
    SCAN_SIDELINE_TO_RESOLVE_PAGE = 'SCAN_SIDELINE_TO_RESOLVE_PAGE',
    SCAN_OWN_LABEL_PAGE = 'SCAN_OWN_LABEL_PAGE'
}

// The unload routes; each has its own state, so no two share a trailer, a pallet or a result
export enum UnloadRoute {
    TRAILER = 'TRAILER',
    PALLET = 'PALLET',
    RESOLVE_SIDELINE = 'RESOLVE_SIDELINE'
}

// What is on screen; the trailer row on the server is the session, so this is never reset on unmount
interface UnloadState {
    // The unload route this state is for
    route: UnloadRoute;
    // The step on screen
    pageToRender: UnloadPages;
    // What a good scan bound
    trailer?: Trailer;
    // A door or pallet scan, and after a good one, while it is the latest result, the loop it opened
    lastScan?: DoorScanResult;
    // The latest label scanned in a loop, with its result
    lastLabel?: LabelScanResult;
    // A sideline not recorded shows on the sideline step, leaving the loop's result as it was
    lastSideline?: LabelScanResult;
    // A close not done, which the loop shows until the next scan
    lastClose?: CloseResult;
    // End Orphan just returned to the step on screen, which says so until the next scan
    orphanEnded: boolean;
    // The trailer or pallet just closed, which the door step shows until another step opens
    closed?: Trailer;
    // A call made and not yet answered; no step opens or closes until it is, so each answer lands where it was made
    pending: boolean;
    // Set when the unload page unmounts, until it is entered again
    pageLeft?: boolean;
    // The latest count re-read, the only one whose answer is used
    latestCountRequestId?: string;
    // The sideline ID being resolved on the own label step
    sidelineToResolve?: string;
    // What the screens call the pallet or trailer that sideline ID is on, when its answer named one
    sidelineParentName?: string;
    // A sideline ID scanned to resolve it that opened no next step
    lastSidelineCheck?: SidelineCheckResult;
    // A resolve not done, which shows on the own label step
    lastResolve?: ResolveResult;
    // The sideline ID just resolved and its label, which the first step shows until the next scan or visit
    lastResolved?: ResolvedSideline;
}

// The parent is the bound trailer's VRID or the open pallet, and an orphan has none
export type LabelScanInput = CreateContainerInput & { locale: string };

// One CreateContainer(mode=UNLOAD): a label under its parent, a sideline ID marked as one, or the label resolving one
export const buildLabelScanInput = (containerId: string, parentContainerId: string | undefined, warehouseId: string,
    associate: string, locale: string, isSideline = false): LabelScanInput => ({ containerId, parentContainerId,
    mode: GetContainerMode.UNLOAD, warehouseId, associate, locale,
    containerProperties: { createReturnContainerPropertiesInput: isSideline ? { isSideline } : {} } });

// The trailer or pallet bound in the loop, which its Close closes
export interface CloseTrailerInput {
    // What the loop has bound
    trailer: Trailer;
    // The associate's site
    warehouseId: string;
    // The associate's id
    associate: string;
    // The associate's locale
    locale: string;
}

type ThunkConfig = { state: { Unload: UnloadState } };
type UnloadThunkDispatch = ThunkDispatch<{ Unload: UnloadState }, unknown, UnknownAction>;

const initialState: UnloadState = { route: UnloadRoute.TRAILER, pageToRender: UnloadPages.SCAN_DOOR_PAGE,
    orphanEnded: false, pending: false };

// A scan made while a call is pending is dropped, as the page ignores it too, so one call is out at a time and its
// answer lands on the step that made it
const noCallPending = (_: unknown, { getState }: { getState: () => { Unload: UnloadState } }) =>
    !getState().Unload.pending;

// The BFF Lambda gives up at 30 s (bff-lambda-stack.ts in RnRIVFrontendWebsiteCDK), so a call still open at 35 s has lost
// its connection. It fails then as timed out, a call with no answer, so one dead connection never holds up
// the next scan
export const NO_ANSWER_MS = 35000;
const withNoAnswerTimeout = <T>(call: Promise<T>): Promise<T> => new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject({ errorMessage: UNLOAD_TIMED_OUT }),
        NO_ANSWER_MS);
    call.then(resolve, reject).finally(() => clearTimeout(timer));
});

type FatalEventContext = { warehouseId: string, associate: string, mode?: string };

// The page shows its own result instead of the shared alert, so it records the FATAL events that alert would, with
// the same helper
const recordCallFatalEvents = (uiAction: RIVApiMethods, input: FatalEventContext, status: number, message?: string) => {
    try {
        recordFatalEvents({ UIAction: uiAction, ErrorStatus: status, ErrorMessage: message,
            Warehouse: input.warehouseId, Associate: input.associate, Mode: input.mode });
    } catch (error) {
        // A metric that fails must not fail the scan
    }
};

// Also ErrorStatus 0 for a call that got no answer. An expired session or a missing permission rejects with a bare
// string and records none, as in every other mode.
const recordFailedCall = (uiAction: RIVApiMethods, input: FatalEventContext, rejection: unknown, message?: string) => {
    if (typeof rejection === 'string') return;
    recordCallFatalEvents(uiAction, input, (rejection as { errorStatus?: number } | null)?.errorStatus ?? 0, message);
};

// A failed call's result, once its FATAL events are recorded
const getRecordedFailure = (uiAction: RIVApiMethods, input: FatalEventContext, value: string, rejection: unknown) => {
    const failure = getFailureResult(value, rejection);
    recordFailedCall(uiAction, input, rejection, failure.message);
    return failure;
};

// An answer the app cannot read fails its scan with the error tone, yet no call failed, so recordFailedCall never sees
// it. It records the same FATAL events with the answer's own status, 200, so a change in what the BFF answers shows
// in the metrics a failed call does
const recordIfUnreadable = <S extends { outcome: string }>(uiAction: RIVApiMethods, input: FatalEventContext,
    scan: S): S => {
    if (scan.outcome === ScanResultType.SYSTEM_ERROR) {
        recordCallFatalEvents(uiAction, input, 200, UNKNOWN_ERROR);
    }
    return scan;
};

// A door scan, or in staging a pallet scan: both are GetContainer(mode=UNLOAD), and the flag picks how to read it
export type DoorScanInput = GetContainerDetailsInput & { pallet?: boolean };

// Sends one GetContainer(mode=UNLOAD) and reads its answer as the scan asks; never rejects, since every answer and
// every failure is a result
const sendGetContainer = async <R extends { outcome: string }>(input: GetContainerDetailsInput,
    readAnswer: (value: string, output: GetContainerDetailsOutput) => R,
    dispatch: UnloadThunkDispatch): Promise<R | ScanResult<FailureResultType>> => {
    dispatch(setIsLoading());
    let output: GetContainerDetailsOutput;
    try {
        output = await withNoAnswerTimeout(RIVBFFService.triggerGetContainerDetailsAPI(input));
    } catch (rejection) {
        return getRecordedFailure(RIVApiMethods.GET_CONTAINER, input, input.containerId, rejection);
    } finally {
        dispatch(resetIsLoading());
    }
    return recordIfUnreadable(RIVApiMethods.GET_CONTAINER, input, readAnswer(input.containerId, output));
};

const doorScanned = createAction<DoorScanResult>('unload/doorScanned');

// The result lands before the call counts as answered, so the next scan sees it, and only on the step that made it: the
// door step of the same unload route, which could have been left only for another route
export const scanDoor = createAsyncThunk<DoorScanResult, DoorScanInput, ThunkConfig>(
    'unload/scanDoor',
    async (input, { dispatch, getState }) => {
        // The door's trailer, or in staging the pallet
        const { pallet, ...call } = input;
        const scan = await sendGetContainer(call, pallet ? getPalletScanResult : getDoorScanResult, dispatch);
        const { pageToRender, route } = getState().Unload;
        if (pageToRender === UnloadPages.SCAN_DOOR_PAGE
            && route === (input.pallet ? UnloadRoute.PALLET : UnloadRoute.TRAILER)) {
            landResult(dispatch, getState, doorScanned, scan);
        }
        return scan;
    },
    { condition: noCallPending }
);

// Re-reads the bound trailer by its VRID, or the open pallet, for its count, which is the server's. RnRFITService's
// GetContainer takes a VRID from the RIV UI in SEARCH mode (ContainerRestrictions.shouldAssumeContainerIdToBeVRID),
// reads a pallet by its label, and recounts the children. It holds up nothing: the next scan is taken as soon as the
// label is answered, and only the latest re-read's answer is used
const fetchChildContainerCount = createAsyncThunk<number | undefined,
    { id: string, pallet: boolean, warehouseId: string, associate: string, locale: string }>(
    'unload/fetchChildContainerCount',
    async ({ id, pallet, warehouseId, associate, locale }) => {
        try {
            const output = await RIVBFFService.triggerGetContainerDetailsAPI(buildGetContainerDetailsInput(id,
                warehouseId, associate, locale, GetContainerMode.SEARCH));
            return getChildContainerCount(output, pallet ? ContainerType.RETURN_PALLET : ContainerType.TRAILER);
        } catch (error) {
            // The re-read failed, and the scan has its result already
            return undefined;
        }
    }
);

// After a scan under the trailer or pallet bound, unless it was refused, since a refusal landed nothing; an orphan has
// no parent to re-read
const refreshCountAfterScan = (dispatch: UnloadThunkDispatch, { trailer }: UnloadState, input: LabelScanInput,
    scan: LabelScanResult) => {
    const id = trailer && getBoundContainerId(trailer);
    if (input.parentContainerId && id && scan.outcome !== ScanResultType.REFUSED) {
        dispatch(fetchChildContainerCount({ id, pallet: trailer?.palletId !== undefined, warehouseId: input.warehouseId,
            associate: input.associate, locale: input.locale }));
    }
};

// Sends one CreateContainer, a label, a sideline ID or the label resolving one, which lands as the outcome given; never
// rejects, since every failure is a result
const sendCreateContainer = <Landed extends ScanResultType>(input: LabelScanInput, landed: Landed,
    dispatch: UnloadThunkDispatch) => {
    dispatch(setIsLoading());
    return withNoAnswerTimeout(RIVBFFService.triggerCreateContainerAPI(input)).then(
        output => recordIfUnreadable(RIVApiMethods.CREATE_CONTAINER, input,
            getLabelScanResult(input.containerId, output, landed)),
        rejection => getRecordedFailure(RIVApiMethods.CREATE_CONTAINER, input, input.containerId, rejection))
        .finally(() => dispatch(resetIsLoading()));
};

const labelScanned = createAction<LabelScanResult>('unload/labelScanned');

// The loop a label was scanned in: the trailer or pallet it names, or with no parent the orphan loop
const isScanLoopStillOpen = ({ pageToRender, trailer }: UnloadState, parent?: string) => parent === undefined
    ? pageToRender === UnloadPages.SCAN_ORPHAN_PAGE
    : pageToRender === UnloadPages.SCAN_CONTAINER_PAGE && (trailer && getBoundContainerId(trailer)) === parent;

// A label shows its result as soon as it is answered, in the loop that sent it alone, then the count is re-read
// whenever the label may have landed; the progress bar is for the scan alone
export const scanLabel = createAsyncThunk<void, LabelScanInput, ThunkConfig>(
    'unload/scanLabel',
    async (input, { dispatch, getState }) => {
        const scan = await sendCreateContainer(input,
            input.parentContainerId ? ScanResultType.RECEIVED : ScanResultType.RECEIVED_AS_ORPHAN, dispatch);
        if (!isScanLoopStillOpen(getState().Unload, input.parentContainerId)) return;
        landResult(dispatch, getState, labelScanned, scan);
        refreshCountAfterScan(dispatch, getState().Unload, input, scan);
    },
    { condition: noCallPending }
);

const sidelineScanned = createAction<LabelScanResult>('unload/sidelineScanned');

// Records the sideline ID of one sidelined container, which the service counts among the children of the trailer or
// pallet it is recorded under, so that count is re-read as after a label
export const scanSideline = createAsyncThunk<LabelScanResult, LabelScanInput, ThunkConfig>(
    'unload/scanSideline',
    async (input, { dispatch, getState }) => {
        const scan = await sendCreateContainer(input, ScanResultType.SIDELINED, dispatch);
        const { pageToRender, trailer } = getState().Unload;
        if (pageToRender === UnloadPages.SCAN_SIDELINE_PAGE && (trailer && getBoundContainerId(trailer)) === input.parentContainerId) {
            landResult(dispatch, getState, sidelineScanned, scan);
            refreshCountAfterScan(dispatch, getState().Unload, input, scan);
        }
        return scan;
    },
    { condition: noCallPending }
);

const sidelineChecked = createAction<SidelineCheckResult>('unload/sidelineChecked');

// Resolve Sideline's first scan, a sideline ID; its result lands on that step alone
export const scanSidelineToResolve = createAsyncThunk<void, GetContainerDetailsInput, ThunkConfig>(
    'unload/scanSidelineToResolve',
    async (input, { dispatch, getState }) => {
        const scan = await sendGetContainer(input, getSidelineCheckResult, dispatch);
        if (getState().Unload.pageToRender === UnloadPages.SCAN_SIDELINE_TO_RESOLVE_PAGE) {
            landResult(dispatch, getState, sidelineChecked, scan);
        }
    },
    { condition: noCallPending }
);

const resolveAnswered = createAction<ResolveResult>('unload/resolveAnswered');

// Resolves the sideline ID with the container's own label, sent with the sideline ID as its parent; the result lands
// while that sideline ID is still the one being resolved
export const resolveSideline = createAsyncThunk<void, LabelScanInput, ThunkConfig>(
    'unload/resolveSideline',
    async (input, { dispatch, getState }) => {
        const scan = await sendCreateContainer(input, ScanResultType.RESOLVED, dispatch);
        const { pageToRender, sidelineToResolve } = getState().Unload;
        if (pageToRender === UnloadPages.SCAN_OWN_LABEL_PAGE && sidelineToResolve === input.parentContainerId) {
            landResult(dispatch, getState, resolveAnswered, scan);
        }
    },
    { condition: noCallPending }
);

const closeAnswered = createAction<CloseResult>('unload/closeAnswered');

// Closes the trailer by its key, or the open pallet, naming it in the result; never rejects, since a failure is the close's result
const sendCloseContainer = async ({ trailer, ...context }: CloseTrailerInput,
    dispatch: UnloadThunkDispatch): Promise<CloseResult> => {
    const id = getBoundContainerId(trailer);
    const name = getBoundContainerName(trailer);
    const input = { ...context, containerId: id, mode: GetContainerMode.UNLOAD };
    dispatch(setIsLoading());
    try {
        const output = await withNoAnswerTimeout(RIVBFFService.triggerCloseContainerAPI(input));
        // CloseContainer answers an empty object, so an answer that is no object at all, such as an HTML page, is a
        // system error, no evidence the close landed
        const outcome = typeof output === 'object' && output !== null
            ? ScanResultType.ACCEPTED : ScanResultType.SYSTEM_ERROR;
        return recordIfUnreadable<CloseResult>(RIVApiMethods.CLOSE_CONTAINER, input, { outcome, value: name });
    } catch (rejection) {
        return getRecordedFailure(RIVApiMethods.CLOSE_CONTAINER, input, name, rejection);
    } finally {
        dispatch(resetIsLoading());
    }
};

// The answer lands only while the trailer or pallet it closes is still the one bound
export const closeTrailer = createAsyncThunk<CloseResult, CloseTrailerInput, ThunkConfig>(
    'unload/closeTrailer',
    async (input, { dispatch, getState }) => {
        const scan = await sendCloseContainer(input, dispatch);
        const { trailer } = getState().Unload;
        if ((trailer && getBoundContainerId(trailer)) === getBoundContainerId(input.trailer)) {
            landClose(dispatch, getState, scan);
        }
        return scan;
    },
    { condition: noCallPending }
);

// Close Trailer and Close Pallet are one tap, with no scan to confirm them, which closeTrailer drops while a call is
// pending
export const closeBoundContainer = () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { Unload: { trailer }, Init: { initData } } = getState();
    if (trailer) {
        dispatch(closeTrailer({ trailer, warehouseId: initData.gradingNodeDetails.id,
            associate: initData.associateDetails.id, locale: initData.associateDetails.locales[0] }));
    }
};

const UNLOAD_CALLS = [scanDoor, scanLabel, scanSideline, scanSidelineToResolve, resolveSideline, closeTrailer] as const;

// The outcomes that sound ok; every other answer sounds the error tone. Each answer sounds as it lands, once
const OK_TONE_RESULT_TYPES = [ScanResultType.ACCEPTED, ScanResultType.RECEIVED, ScanResultType.RECEIVED_AS_ORPHAN,
    ScanResultType.SIDELINED, ScanResultType.RESOLVED];

const getToneForResult = (outcome: ScanResultType): ScanTone => (OK_TONE_RESULT_TYPES.includes(outcome) ? 'ok' : 'err');

// A result lands, and sounds, only while the unload page is open: an answer that lands after the page was left answers
// a scan on a visit that is gone
const landResult = <R extends { outcome: ScanResultType }>(dispatch: (action: PayloadAction<R>) => unknown,
    getState: () => { Unload: UnloadState }, resultAnswered: (result: R) => PayloadAction<R>, result: R) => {
    if (getState().Unload.pageLeft) return;
    dispatch(resultAnswered(result));
    playScanTone(getToneForResult(result.outcome));
};

// A close's answer lands even after the page was left, since a close the server made ends the trailer or pallet here too, so
// the next visit opens on the door step; it sounds only on the page, and the next visit clears what it shows
const landClose = (dispatch: (action: PayloadAction<CloseResult>) => unknown, getState: () => { Unload: UnloadState },
    scan: CloseResult) => {
    if (getState().Unload.pageLeft) {
        dispatch(closeAnswered(scan));
    } else {
        landResult(dispatch, getState, closeAnswered, scan);
    }
};

// Clears every result on screen, leaving the step, what is bound and the sideline ID being resolved
const clearResults = (state: UnloadState) => {
    state.lastScan = undefined;
    state.lastLabel = undefined;
    state.lastSideline = undefined;
    state.lastClose = undefined;
    state.orphanEnded = false;
    state.closed = undefined;
    state.lastSidelineCheck = undefined;
    state.lastResolve = undefined;
    state.lastResolved = undefined;
};

// Each step opens with no result on it; a bound trailer stays bound
const openStep = (state: UnloadState, page: UnloadPages) => {
    state.pageToRender = page;
    clearResults(state);
    state.sidelineToResolve = undefined;
    state.sidelineParentName = undefined;
};

// The step the orphan loop and the sideline step return to: the loop of the trailer or pallet still bound, or the door
// or pallet step when none is
const getReturnStep = ({ trailer }: UnloadState) =>
    (trailer ? UnloadPages.SCAN_CONTAINER_PAGE : UnloadPages.SCAN_DOOR_PAGE);

const unloadSlice = createSlice({
    name: 'unload',
    initialState,
    reducers: {
        // The unload page unmounted: an answer that lands before it is entered again shows nothing and sounds nothing
        leaveUnloadRoute: state => {
            state.pageLeft = true;
        },
        // A step opened or closed while a call is pending is ignored, here and in the three below
        startOrphan: state => {
            if (!state.pending) openStep(state, UnloadPages.SCAN_ORPHAN_PAGE);
        },
        // Back from the own label step to Resolve Sideline's first step
        returnToSidelineId: state => {
            if (!state.pending) openStep(state, UnloadPages.SCAN_SIDELINE_TO_RESOLVE_PAGE);
        },
        // End Orphan opens the step the orphan loop returns to, which says the orphan loop ended
        endOrphan: state => {
            if (state.pending) return;
            openStep(state, getReturnStep(state));
            state.orphanEnded = true;
        },
        // The sideline step opens empty over the loop, and closing it leaves the loop as it was
        showSideline: (state, { payload }: PayloadAction<boolean>) => {
            if (state.pending) return;
            state.pageToRender = payload ? UnloadPages.SCAN_SIDELINE_PAGE : getReturnStep(state);
            state.lastSideline = undefined;
        },
        // Each visit opens with no result on screen and the step and trailer it left; another route starts afresh.
        // A call still out stays pending, so no other goes out until it is answered; on the same route its answer
        // lands on this visit, since it answers a scan made on the step this visit opens
        enterUnloadRoute: (state, { payload: route }: PayloadAction<UnloadRoute>) => {
            if (route !== state.route) {
                // Resolve Sideline opens on its sideline ID, the other routes on their door or pallet step
                return { ...initialState, route, pending: state.pending, pageToRender: route === UnloadRoute.RESOLVE_SIDELINE
                    ? UnloadPages.SCAN_SIDELINE_TO_RESOLVE_PAGE : initialState.pageToRender };
            }
            state.pageLeft = undefined;
            clearResults(state);
        }
    },
    extraReducers: builder => {
        // A good door or pallet scan opens the loop; a failed one binds nothing and leaves what is bound
        builder.addCase(doorScanned, (state, { payload }) => {
            if (payload.trailer) {
                openStep(state, UnloadPages.SCAN_CONTAINER_PAGE);
                state.trailer = payload.trailer;
                // A re-read still out was sent for what was bound before, and this scan's count is newer
                state.latestCountRequestId = undefined;
            }
            state.lastScan = payload;
        }).addCase(labelScanned, (state, { payload }) => {
            state.lastLabel = payload;
            state.lastScan = undefined;
            state.lastClose = undefined;
            state.orphanEnded = false;
        }).addCase(fetchChildContainerCount.pending, (state, { meta }) => {
            state.latestCountRequestId = meta.requestId;
        }).addCase(fetchChildContainerCount.fulfilled, (state, { payload, meta }) => {
            // Used only from the latest re-read, with a count, for the trailer or pallet bound here or just closed
            const target = state.trailer ?? state.closed;
            if (meta.requestId === state.latestCountRequestId && target && payload !== undefined) {
                target.count = payload;
            }
        }).addCase(sidelineScanned, (state, { payload }) => {
            if (payload.outcome === ScanResultType.SIDELINED) {
                // Back to the loop, which shows it
                state.lastLabel = payload;
                state.lastScan = undefined;
                state.lastClose = undefined;
                state.orphanEnded = false;
                state.pageToRender = getReturnStep(state);
            } else {
                state.lastSideline = payload;
            }
        }).addCase(sidelineChecked, (state, { payload }) => {
            // A sideline ID on a current trailer or pallet opens the step that resolves it; any other result shows here
            state.lastResolved = undefined;
            const found = payload.outcome === ScanResultType.ACCEPTED;
            state.pageToRender = found ? UnloadPages.SCAN_OWN_LABEL_PAGE : state.pageToRender;
            state.sidelineToResolve = found ? payload.value : undefined;
            state.sidelineParentName = found ? payload.parentName : undefined;
            state.lastSidelineCheck = found ? undefined : payload;
        }).addCase(resolveAnswered, (state, { payload }) => {
            // Resolved, the first step opens again and shows it; a resolve not done stays on this step
            if (payload.outcome === ScanResultType.RESOLVED) {
                const sidelineId = state.sidelineToResolve!;
                openStep(state, UnloadPages.SCAN_SIDELINE_TO_RESOLVE_PAGE);
                state.lastResolved = { sidelineId, containerId: payload.value };
            } else {
                state.lastResolve = payload;
            }
        }).addCase(closeAnswered, (state, { payload }) => {
            if (payload.outcome === ScanResultType.ACCEPTED && state.trailer) {
                // A closed trailer or pallet is gone, so the door step opens with nothing bound, showing what was closed
                return { ...initialState, route: state.route, pending: state.pending,
                    latestCountRequestId: state.latestCountRequestId, closed: state.trailer };
            }
            state.lastClose = payload;
        }).addMatcher(isPending(...UNLOAD_CALLS), state => {
            state.pending = true;
        }).addMatcher(isFulfilled(...UNLOAD_CALLS), state => {
            state.pending = false;
        }).addMatcher(isRejected(...UNLOAD_CALLS), state => {
            state.pending = false;
        });
    }
});

export const { startOrphan, returnToSidelineId, endOrphan, showSideline, enterUnloadRoute,
    leaveUnloadRoute } = unloadSlice.actions;
export default unloadSlice.reducer;
